import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  format,
  addWeeks,
  subWeeks,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isToday,
  isSameDay,
  isPast,
  setHours,
  setMinutes,
  isWithinInterval,
  addMinutes,
  parse,
} from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import MeetingCard from './MeetingCard';
import BookMeetingDialog from './BookMeetingDialog';
import MeetingDetailsDialog from './MeetingDetailsDialog';
import LoadingSpinner from '../common/LoadingSpinner';
import { calendarService } from '../../services/calendarService';
import { businessConfigService } from '../../services/businessConfigService';
import { isSlotBookable } from '../../utils/slotValidation';
import { findFirstAvailableSlot } from '../../utils/findFirstAvailableSlot';

/**
 * Working Week Calendar - Shows only working days and hours based on calendar configuration
 */
const WorkingWeekCalendar = ({ customerId, selectedCalendar }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'pl' ? pl : enUS;
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [events, setEvents] = useState({});
  const [calendars, setCalendars] = useState([]);
  const [activeCalendar, setActiveCalendar] = useState(selectedCalendar || null);
  const [calendarConfig, setCalendarConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBookDialog, setShowBookDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [prefilledDate, setPrefilledDate] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const initializedRef = useRef(false);

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 }); // Monday

  // Early return if no calendar selected
  if (!activeCalendar) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Please select a calendar to view working week
        </Typography>
        {calendars.length > 0 && (
          <FormControl sx={{ mt: 2, minWidth: 300 }}>
            <InputLabel>Select Calendar</InputLabel>
            <Select
              value=""
              label="Select Calendar"
              onChange={(e) => setActiveCalendar(e.target.value)}
            >
              {calendars.map((cal) => (
                <MenuItem key={cal.name} value={cal.name}>
                  {cal.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    );
  }

  // Parse business hours from calendar config
  const getWorkingDays = () => {
    const availabilityRules = calendarConfig?.business_hours?.availability_rules;
    if (!availabilityRules) {
      // Default to Mon-Fri if not configured
      return [1, 2, 3, 4, 5]; // Monday to Friday
    }

    const workingDays = [];
    const dayMap = {
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sunday: 0,
    };

    const workingDayNames = availabilityRules.days || [];
    workingDayNames.forEach((day) => {
      const dayNum = dayMap[day.toLowerCase()];
      if (dayNum !== undefined) {
        workingDays.push(dayNum);
      }
    });

    return workingDays.length > 0 ? workingDays.sort((a, b) => a - b) : [1, 2, 3, 4, 5];
  };

  const getWorkingHours = (dayOfWeek) => {
    const availabilityRules = calendarConfig?.business_hours?.availability_rules;
    if (!availabilityRules) {
      return { start: '09:00', end: '17:00' };
    }

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek].toLowerCase();
    const workingDays = availabilityRules.days || [];

    if (!workingDays.includes(dayName)) {
      return { start: '09:00', end: '17:00' };
    }

    return {
      start: availabilityRules.hours_start || '09:00',
      end: availabilityRules.hours_end || '17:00'
    };
  };

  const workingDays = getWorkingDays();
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
  const workingWeekDays = workingDays.length > 0 
    ? eachDayOfInterval({ start: weekStart, end: weekEnd }).filter(day => workingDays.includes(day.getDay()))
    : [];

  useEffect(() => {
    const fetchCalendars = async () => {
      try {
        const calendarList = await businessConfigService.getBusinessCalendars(customerId);
        setCalendars(calendarList);

        // Auto-select first calendar if none selected
        if (!activeCalendar && calendarList.length > 0) {
          const firstCalendar = calendarList[0];
          setActiveCalendar(firstCalendar.name);
          
          // Find and navigate to first available slot
          const firstAvailableDate = findFirstAvailableSlot(firstCalendar);
          setCurrentWeek(firstAvailableDate);
        }
      } catch (err) {
        console.error('Error fetching calendars:', err);
      }
    };

    if (customerId) {
      fetchCalendars();
    }
  }, [customerId]);

  useEffect(() => {
    const fetchCalendarConfig = async () => {
      if (!activeCalendar) return;

      try {
        const config = calendars.find(cal => cal.name === activeCalendar);
        setCalendarConfig(config);
        
        // Find and navigate to first available slot ONLY on initial load
        if (config && !initializedRef.current) {
          const firstAvailableDate = findFirstAvailableSlot(config);
          setCurrentWeek(firstAvailableDate);
          initializedRef.current = true;
        }
      } catch (err) {
        console.error('Error fetching calendar config:', err);
      }
    };

    if (activeCalendar) {
      fetchCalendarConfig();
    }
  }, [activeCalendar, calendars]);

  useEffect(() => {
    const fetchEvents = async () => {
      if (!activeCalendar) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const customersData = await calendarService.getWeekEvents(customerId, currentWeek);
        
        // Organize events by day and time slot
        const eventsByDay = {};
        workingWeekDays.forEach(day => {
          eventsByDay[format(day, 'yyyy-MM-dd')] = [];
        });

        // Parse events from nested structure: {customerId: {business_name, calendars: {calendarName: {data}}}}
        Object.entries(customersData).forEach(([custId, customerInfo]) => {
          const calendars = customerInfo.calendars || {};
          const calendarData = calendars[activeCalendar];
          
          if (calendarData) {
            const calendarEvents = calendarData.calendar_events || [];
            calendarEvents.forEach(event => {
              const parsedEvent = calendarService.parseEvent(event);
              if (parsedEvent) {
                const eventWithCalendar = {
                  ...parsedEvent,
                  calendarName: activeCalendar,
                  customerId: custId,
                  businessName: customerInfo.business_name,
                };
                const dayKey = format(parsedEvent.start, 'yyyy-MM-dd');
                if (eventsByDay[dayKey]) {
                  eventsByDay[dayKey].push(eventWithCalendar);
                }
              }
            });
          }
        });

        // Sort events within each day
        Object.keys(eventsByDay).forEach(day => {
          eventsByDay[day].sort((a, b) => a.start - b.start);
        });

        setEvents(eventsByDay);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch events if we have customerId, activeCalendar AND calendarConfig
    // This prevents a race condition where events are filtered out before config loads
    if (customerId && activeCalendar && calendarConfig) {
      fetchEvents();
    }
  }, [customerId, currentWeek, activeCalendar, calendarConfig]);

  const handlePreviousWeek = () => {
    setCurrentWeek(subWeeks(currentWeek, 1));
  };

  const handleNextWeek = () => {
    setCurrentWeek(addWeeks(currentWeek, 1));
  };

  const handleToday = () => {
    setCurrentWeek(new Date());
  };

  const handleMeetingClick = (meeting) => {
    setSelectedMeeting(meeting);
    setShowDetailsDialog(true);
  };

  const handleTimeSlotClick = (day, timeSlot) => {
    const [hours, minutes] = timeSlot.split(':').map(Number);
    const slotDate = setMinutes(setHours(day, hours), minutes);
    setPrefilledDate(slotDate);
    setShowBookDialog(true);
  };

  // Drag and drop handlers
  const handleDragStart = (e, event) => {
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, day, timeSlot) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const [hours, minutes] = timeSlot.split(':').map(Number);
    const slotDateTime = setMinutes(setHours(day, hours), minutes);
    const { isBookable } = isSlotBookable(slotDateTime, calendarConfig, t);
    
    if (isBookable && draggedEvent) {
      setDropTarget(`${format(day, 'yyyy-MM-dd')}-${timeSlot}`);
    }
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = (e, day, timeSlot) => {
    e.preventDefault();
    setDropTarget(null);

    if (!draggedEvent) return;

    // Create new start time from drop target
    const [hours, minutes] = timeSlot.split(':').map(Number);
    const newStartTime = setMinutes(setHours(day, hours), minutes);

    // Validate the drop target is bookable
    const { isBookable } = isSlotBookable(newStartTime, calendarConfig, t);
    if (!isBookable) {
      alert(t('booking.slotNotAvailableForBooking'));
      setDraggedEvent(null);
      return;
    }

    // Open rebook dialog with pre-filled date/time
    setSelectedMeeting(draggedEvent);
    setPrefilledDate(newStartTime);
    setShowBookDialog(true);
    setDraggedEvent(null);
  };

  const handleMeetingUpdated = () => {
    // Refresh events
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const customersData = await calendarService.getWeekEvents(customerId, currentWeek);
        
        const eventsByDay = {};
        workingWeekDays.forEach(day => {
          eventsByDay[format(day, 'yyyy-MM-dd')] = [];
        });

        // Parse events from nested structure
        Object.entries(customersData).forEach(([custId, customerInfo]) => {
          const calendars = customerInfo.calendars || {};
          const calendarData = calendars[activeCalendar];
          
          if (calendarData) {
            const calendarEvents = calendarData.calendar_events || [];
            calendarEvents.forEach(event => {
              const parsedEvent = calendarService.parseEvent(event);
              if (parsedEvent) {
                const eventWithCalendar = {
                  ...parsedEvent,
                  calendarName: activeCalendar,
                  customerId: custId,
                  businessName: customerInfo.business_name,
                };
                const dayKey = format(parsedEvent.start, 'yyyy-MM-dd');
                if (eventsByDay[dayKey]) {
                  eventsByDay[dayKey].push(eventWithCalendar);
                }
              }
            });
          }
        });

        Object.keys(eventsByDay).forEach(day => {
          eventsByDay[day].sort((a, b) => a.start - b.start);
        });

        setEvents(eventsByDay);
      } catch (err) {
        console.error('Error refreshing events:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  };

  // Generate time slots based on business hours
  const generateTimeSlots = (day) => {
    const dayOfWeek = day.getDay();
    const workingHours = getWorkingHours(dayOfWeek);
    
    const [startHour, startMinute] = workingHours.start.split(':').map(Number);
    const [endHour, endMinute] = workingHours.end.split(':').map(Number);
    const slotDuration = parseInt(calendarConfig?.general?.granularity) || 30; // Default to 30 minutes
    
    const slots = [];
    let currentTime = setMinutes(setHours(day, startHour), startMinute);
    const endTime = setMinutes(setHours(day, endHour), endMinute);
    
    while (currentTime < endTime) {
      slots.push(format(currentTime, 'HH:mm'));
      currentTime = addMinutes(currentTime, slotDuration);
    }
    
    return slots;
  };

  // Find events for a specific time slot
  const getEventsForSlot = (day, timeSlot) => {
    const dayKey = format(day, 'yyyy-MM-dd');
    const dayEvents = events[dayKey] || [];
    const slotDuration = parseInt(calendarConfig?.general?.granularity) || 30; // Default to 30 minutes
    
    const [hours, minutes] = timeSlot.split(':').map(Number);
    const slotStart = setMinutes(setHours(day, hours), minutes);
    const slotEnd = addMinutes(slotStart, slotDuration);
    
    return dayEvents.filter(event => {
      // Event overlaps with slot if:
      // 1. Event starts during this slot (event.start >= slotStart && event.start < slotEnd)
      // 2. Slot starts during the event (slotStart >= event.start && slotStart < event.end)
      return (event.start >= slotStart && event.start < slotEnd) ||
             (slotStart >= event.start && slotStart < event.end);
    });
  };

  // Check if event starts in this specific slot
  const eventStartsInSlot = (event, day, timeSlot) => {
    const [hours, minutes] = timeSlot.split(':').map(Number);
    const slotStart = setMinutes(setHours(day, hours), minutes);
    const slotEnd = addMinutes(slotStart, 60);
    
    return event.start >= slotStart && event.start < slotEnd;
  };

  // Calculate how many 40px slots this event spans (60-minute slots)
  const calculateEventHeight = (event) => {
    const durationMinutes = (event.end - event.start) / 60000;
    const slotDuration = parseInt(calendarConfig?.general?.granularity) || 30;
    const slots = Math.ceil(durationMinutes / slotDuration);
    return slots * 40; // 40px per slot
  };

  if (!calendarConfig || workingWeekDays.length === 0) {
    return loading ? <LoadingSpinner /> : (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No working days configured for this calendar
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handlePreviousWeek}>
              <ChevronLeftIcon />
            </IconButton>
            
            <Typography variant="h5" component="div">
              {workingWeekDays.length > 0
                ? `${format(workingWeekDays[0], 'MMM d', { locale: dateLocale })} - ${format(workingWeekDays[workingWeekDays.length - 1], 'MMM d, yyyy', { locale: dateLocale })}`
                : format(weekStart, 'MMM d, yyyy', { locale: dateLocale })}
            </Typography>

            <IconButton onClick={handleNextWeek}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<TodayIcon />}
              onClick={handleToday}
              size="small"
              sx={{ height: 40 }}
            >
              {t('common.today')}
            </Button>

            {calendars.length > 1 && (
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>Calendar</InputLabel>
                <Select
                  value={activeCalendar}
                  label="Calendar"
                  onChange={(e) => setActiveCalendar(e.target.value)}
                  sx={{ height: 40 }}
                >
                  {calendars.map((cal) => (
                    <MenuItem key={cal.name} value={cal.name}>
                      {cal.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Working Week Grid */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <Typography color="error" align="center">{error}</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Grid container spacing={0} sx={{ minWidth: 800 }}>
            {/* Time column header */}
            <Grid item xs={1.5}>
              <Paper sx={{ 
                p: 1,
                minHeight: 64,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center', 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200',
                borderBottom: 2,
                borderColor: 'divider'
              }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('common.time')}
                </Typography>
              </Paper>
            </Grid>

            {/* Day headers */}
            {workingWeekDays.map((day) => (
              <Grid item xs={(12 - 1.5) / workingWeekDays.length} key={format(day, 'yyyy-MM-dd')}>
                <Paper
                  sx={{
                    p: 1,
                    minHeight: 64,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    bgcolor: isToday(day) 
                      ? 'primary.main' 
                      : (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200',
                    borderBottom: 2,
                    borderColor: 'divider',
                    color: isToday(day) ? 'primary.contrastText' : 'text.primary'
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={isToday(day) ? 'bold' : 'normal'}>
                    {format(day, 'EEE', { locale: dateLocale })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isToday(day) ? 'primary.contrastText' : 'text.secondary' }}>
                    {format(day, 'MMM d', { locale: dateLocale })}
                  </Typography>
                </Paper>
              </Grid>
            ))}

            {/* Time slots grid */}
            {workingWeekDays.length > 0 && generateTimeSlots(workingWeekDays[0]).map((timeSlot) => (
              <Grid container item xs={12} key={timeSlot} spacing={0}>
                {/* Time label */}
                <Grid item xs={1.5}>
                  <Paper
                    sx={{
                      p: 1,
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderTop: 1,
                      borderColor: 'divider',
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
                    }}
                  >
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {timeSlot}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Day slots */}
                {workingWeekDays.map((day) => {
                  const slotEvents = getEventsForSlot(day, timeSlot);
                  const slotStartEvents = slotEvents.filter(event => eventStartsInSlot(event, day, timeSlot));
                  const isDayToday = isToday(day);
                  
                  // Create slot datetime for validation
                  const [hours, minutes] = timeSlot.split(':').map(Number);
                  const slotDateTime = setMinutes(setHours(day, hours), minutes);
                  const { isBookable, reason } = isSlotBookable(slotDateTime, calendarConfig, t);
                  
                  // Check if this slot is covered by an event (but event doesn't start here)
                  const isCoveredByEvent = slotEvents.length > 0 && slotStartEvents.length === 0;
                  
                  // Check if this is the current drop target
                  const isDropTarget = dropTarget === `${format(day, 'yyyy-MM-dd')}-${timeSlot}`;
                  
                  return (
                    <Grid item xs={(12 - 1.5) / workingWeekDays.length} key={`${format(day, 'yyyy-MM-dd')}-${timeSlot}`} sx={{ overflow: slotStartEvents.length > 0 ? 'visible' : 'hidden', position: 'relative', zIndex: slotStartEvents.length > 0 ? 1 : 0 }}>
                      <Paper
                        title={isCoveredByEvent ? t('booking.coveredByMeeting') : (!isBookable ? reason : t('booking.clickToBook'))}
                        onDragOver={(e) => handleDragOver(e, day, timeSlot)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, day, timeSlot)}
                        sx={{
                          p: (isBookable && !isCoveredByEvent) ? 0.5 : 0,
                          height: '40px',
                          position: 'relative',
                          borderTop: 1,
                          borderLeft: 1,
                          borderColor: 'divider',
                          cursor: (isBookable && !isCoveredByEvent) ? 'pointer' : 'not-allowed',
                          bgcolor: isDropTarget
                            ? 'primary.light'
                            : isCoveredByEvent
                            ? (theme) => theme.palette.mode === 'dark' ? 'grey.850' : 'grey.200'
                            : !isBookable
                            ? (theme) => theme.palette.mode === 'dark' ? 'grey.850' : 'grey.200'
                            : slotStartEvents.length > 0 
                              ? (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'action.hover'
                              : (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper',
                          opacity: isDropTarget ? 0.5 : 1,
                          overflow: slotStartEvents.length > 0 ? 'visible' : 'hidden',
                          '&:hover': {
                            bgcolor: !isBookable
                              ? (theme) => theme.palette.mode === 'dark' ? 'grey.850' : 'grey.200'
                              : (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'action.hover',
                          },
                        }}
                        onClick={() => isBookable && !isCoveredByEvent && slotStartEvents.length === 0 && handleTimeSlotClick(day, timeSlot)}
                      >
                        {slotStartEvents.length === 0 && !isCoveredByEvent && (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', position: 'relative', zIndex: 1 }}>
                            <Typography variant="caption" color={!isBookable ? 'text.disabled' : 'text.secondary'} sx={{ fontSize: '0.65rem', opacity: !isBookable ? 0.5 : 1 }}>
                              {!isBookable ? t('booking.unavailable') : t('booking.available')}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Render events that start in this slot */}
                        {slotStartEvents.length > 0 && (
                          slotStartEvents
                            .map((event) => {
                              const eventHeight = calculateEventHeight(event);
                              const durationMinutes = (event.end - event.start) / 60000;
                              const isEventPast = isPast(event.end);
                              const isConfirmed = event.confirmationStatus === 'confirmed';
                              const isPending = event.confirmationStatus === 'pending';
                              const typeLabel = event.typeDescription || event.type || event.summary || 'Meeting';
                              const patientName = event.customerName || '';
                              const timeRange = `${format(event.start, 'h:mm a')} - ${format(event.end, 'h:mm a')}`;
                              const secondLine = patientName ? `${timeRange} ${patientName}` : timeRange;
                              
                              return (
                                <Tooltip
                                  key={event.id}
                                  title={
                                    <Box>
                                      <Typography variant="caption" fontWeight="bold">
                                        {typeLabel}
                                      </Typography>
                                      <Typography variant="caption">
                                        {secondLine}
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Box
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, event)}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMeetingClick(event);
                                    }}
                                    sx={{ 
                                      position: 'absolute',
                                      top: 4,
                                      left: 4,
                                      right: 4,
                                      height: `${eventHeight - 8}px`,
                                      zIndex: 100,
                                      opacity: draggedEvent?.id === event.id ? 0.5 : 1,
                                    }}
                                  >
                                    <Paper
                                      elevation={isEventPast ? 1 : 3}
                                      sx={{
                                        height: '100%',
                                        p: 0.5,
                                        bgcolor: isEventPast 
                                          ? (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.400'
                                          : isConfirmed
                                            ? 'success.main'
                                            : isPending
                                              ? 'warning.main'
                                              : event.isCustomerMeeting
                                                ? 'primary.main'
                                                : 'secondary.main',
                                        color: isEventPast
                                          ? 'text.disabled'
                                          : isConfirmed || isPending
                                            ? 'common.white'
                                            : event.isCustomerMeeting
                                              ? 'primary.contrastText'
                                              : 'secondary.contrastText',
                                        cursor: 'move',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        '&:hover': {
                                          bgcolor: isEventPast
                                            ? (theme) => theme.palette.mode === 'dark' ? 'grey.600' : 'grey.500'
                                            : isConfirmed
                                              ? 'success.dark'
                                              : isPending
                                                ? 'warning.dark'
                                                : event.isCustomerMeeting
                                                  ? 'primary.dark'
                                                  : 'secondary.dark',
                                        },
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                                        <Typography variant="caption" noWrap fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                                          {typeLabel}
                                        </Typography>
                                        {isConfirmed && (
                                          <CheckCircleIcon sx={{ fontSize: '0.9rem' }} />
                                        )}
                                      </Box>
                                      <Typography variant="caption" noWrap display="block" sx={{ fontSize: '0.6rem' }}>
                                        {secondLine}
                                      </Typography>
                                    </Paper>
                                  </Box>
                                </Tooltip>
                              );
                        })
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Dialogs */}
      <BookMeetingDialog
        open={showBookDialog}
        onClose={(success) => {
          setShowBookDialog(false);
          setPrefilledDate(null);
          setSelectedMeeting(null);
          if (success) {
            handleMeetingUpdated();
          }
        }}
        customerId={customerId}
        calendarName={activeCalendar}
        prefilledDate={prefilledDate}
        existingMeeting={selectedMeeting}
        isRescheduling={!!selectedMeeting}
      />

      <MeetingDetailsDialog
        open={showDetailsDialog}
        onClose={() => {
          setShowDetailsDialog(false);
          setSelectedMeeting(null);
        }}
        meeting={selectedMeeting}
        customerId={customerId}
        onMeetingUpdated={handleMeetingUpdated}
      />
    </Box>
  );
};

export default WorkingWeekCalendar;
